import { z } from "zod";
import PageHeader from "@/components/PageHeader";
import FormBuilder from "@/components/forms/FormBuilder";
import PrivacyLabel from "@/components/forms/PrivacyLabel";
import { usePageMeta } from "@/hooks/usePageMeta";

const schema = z.object({
  full_name: z.string().min(1, "Please enter your full name"),
  email: z.string().min(1, "Please enter your email").email("Please enter a valid email address"),
  telephone: z.string().min(6, "Please enter a telephone number"),
  area: z.string().min(1, "Please tell us the general area you can work in"),
  driver: z.enum(["Yes", "No"], { errorMap: () => ({ message: "Please let us know if you are a driver" }) }),
  experience: z.string().min(1, "Please tell us about any relevant experience"),
  availability: z.string().min(1, "Please tell us about your availability"),
  dbs_confirm: z
    .boolean()
    .refine((v) => v === true, "Please confirm your willingness to undergo a DBS check where appropriate"),
  privacy: z.boolean().refine((v) => v === true, "Please acknowledge the Privacy Notice to continue"),
  company: z.string().optional(),
});

const fields = [
  { name: "full_name", label: "Full name", type: "text", autoComplete: "name" },
  { name: "email", label: "Email", type: "email", width: "half", autoComplete: "email" },
  { name: "telephone", label: "Telephone", type: "tel", width: "half", autoComplete: "tel" },
  { name: "area", label: "General area available to work", type: "text" },
  { name: "driver", label: "Are you a driver?", type: "radio", options: ["Yes", "No"] },
  {
    name: "experience",
    label: "Relevant experience",
    type: "textarea",
    rows: 4,
    placeholder: "Any experience in fundraising, sales, charity work or public-facing roles.",
  },
  {
    name: "availability",
    label: "Availability",
    type: "text",
    placeholder: "For example: weekdays, weekends, full-time, part-time.",
  },
  {
    name: "dbs_confirm",
    label: "DBS confirmation",
    type: "checkbox",
    labelNode: (
      <>I confirm I am willing to undergo a DBS check where appropriate.</>
    ),
  },
  { name: "privacy", label: "Privacy acknowledgement", type: "checkbox", labelNode: <PrivacyLabel /> },
];

export default function FundraiserPage() {
  usePageMeta(
    "Join Our Fundraising Team | Amanah Promotions",
    "Register your interest in professional, respectful face-to-face fundraising with Amanah Promotions."
  );
  return (
    <>
      <PageHeader
        eyebrow="Join Our Fundraising Team"
        title="Represent great causes with integrity"
        intro="If you enjoy meaningful conversations and respectful public engagement, we would like to hear from you. Share your details and we will be in touch."
      />
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10 sm:py-20 lg:px-16">
          <FormBuilder
            fields={fields}
            schema={schema}
            endpoint="/forms/fundraiser"
            submitLabel="Register My Interest"
            defaultValues={{ driver: "", dbs_confirm: false, privacy: false }}
            success={{
              title: "Thank you — we've received your details",
              message:
                "Thank you for your interest in joining the Amanah fundraising team. We will review your details and be in touch.",
            }}
          />
        </div>
      </section>
    </>
  );
}
